import {
  View,
  Text,
  Image,
  StyleSheet,
  Pressable,
  Platform,
  Linking,
  ActivityIndicator,
  TouchableOpacity,
  Modal,
  Animated,
} from 'react-native';
import { ChargerAttributes } from '../models/charger-model';
import ChargerStatusBadge from '../components/ChargerStatusBadge';
import { getDeviceDimensions } from '../components/common/utils/platform.util';
import { NavigationProp } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/stackTypes';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { chargerActions } from '../store/slices/charger-slice';
import { getUserLocation } from '../components/common/utils/get-user-location';
import { getMyDistanceFromCharger, getMyDrivingTime } from '../store/actions/charging-actions';
import { useState, useRef } from 'react';
import * as Haptics from 'expo-haptics';
import * as Location from 'expo-location';
//@ts-ignore
import SparklineChart, { SparklineDataPoint } from '../components/graphs/SparklineChart';
import ChargerImageLoader from '../components/ChargerImageLoader';
import { SplitBadge, SplitListModal } from '../components/RevenueSplit';
import { Ionicons } from '@expo/vector-icons';

const { deviceWidth } = getDeviceDimensions();

const MyChargerInfoCard = (props: {
  index: number;
  data: ChargerAttributes;
  editCharger: (chargerDetails: any) => void;
  showHistory: (chargerId: string) => void;
  navigation: NavigationProp<RootStackParamList>;
  sparklineData?: SparklineDataPoint[];
  onInsightsPress?: (chargerId: string) => void;
}) => {
  const dispatch: AppDispatch = useDispatch();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showSplitsModal, setShowSplitsModal] = useState(false);
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);
  const [showPendingInfoModal, setShowPendingInfoModal] = useState(false);

  const imageScale = useRef(new Animated.Value(1)).current;
  const nameScale = useRef(new Animated.Value(1)).current;
  const editScale = useRef(new Animated.Value(1)).current;
  const historyScale = useRef(new Animated.Value(1)).current;
  const insightsScale = useRef(new Animated.Value(1)).current;

  const animatePressIn = (scale: Animated.Value) => {
    try { Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); } catch {}
    Animated.spring(scale, {
      toValue: 0.94,
      useNativeDriver: true,
    }).start();
  };

  const animatePressOut = (scale: Animated.Value) => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 3,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  // Check if the charger is in pending registration status
  const isPendingRegistration = props.data?.registration_status === 'pending_charger';
  
  // Scheme for opening maps
  const label = encodeURIComponent(props?.data.name);
  const lat = props?.data.lat;
  const lng = props?.data.lng;
  const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  const scheme = Platform.select({
    ios: `maps://?q=${label}&ll=${lat},${lng}`,
    android: `geo:${lat},${lng}?q=${lat},${lng}(${label})`,
  });

  /**
   * Handle charger click event.
   *
   * If the device supports opening native maps,
   * open the native maps app with the charger location.
   * Otherwise, open the location in the browser.
   */
  const handleChargerClick = () => {
    Linking.canOpenURL(`{scheme}`).then((supported) => {
      if (supported) {
        return Linking.openURL(`${scheme}`);
      } else {
        Linking.openURL(fallbackUrl);
      }
    });
  };

  /**
   * Handle navigation to ChargerStation.
   * Gets user location, calculates distance and driving time, 
   * sets the selected charger in the Redux store, and navigates to ChargerStation.
   */
  const handleNavigateToCharger = async () => {
    if (isNavigating) return; // Prevent multiple concurrent navigation attempts
    
    setIsNavigating(true);
    
    try {
      // Set the selected charger in the Redux store first
      dispatch(chargerActions.updateSelectedCharger({
        selectedCharger: props.data
      }));
      
      // Get user location
      const myLocation: Location.LocationObjectCoords = await getUserLocation();
      
      // Calculate distance and driving time
      const myDistance: number = getMyDistanceFromCharger(myLocation, props.data);
      const myDrivingTime: string = await getMyDrivingTime(myLocation, props.data.uuid!);
      
      // Navigate to ChargerStation with the calculated data
      props.navigation.navigate('ChargerStation', {
        myDistanceFromCharger: myDistance.toString(),
        myTimeFromCharger: myDrivingTime,
        myLocation: myLocation,
      });
    } catch (error) {
      console.error('Error navigating to charger:', error);
      // Fallback navigation without location data
      props.navigation.navigate('ChargerStation', {});
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <View
      className="mt-4 rounded-lg"
      key={props.index}
      style={{ backgroundColor: '#292D33' }}
    >
      <View className="flex flex-row justify-between items-center p-3">
        <View className="flex flex-row flex-1 gap-4 items-center">
          <Animated.View style={{ transform: [{ scale: imageScale }] }}>
            <Pressable
              onPress={handleNavigateToCharger}
              disabled={isNavigating}
              onPressIn={() => animatePressIn(imageScale)}
              onPressOut={() => animatePressOut(imageScale)}
              style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
            >
              {isNavigating ? (
                <View style={{ width: 50, height: 50, justifyContent: 'center', alignItems: 'center' }}>
                  <ActivityIndicator size="small" color="#39BEB7" />
                </View>
              ) : (
                <ChargerImageLoader
                  chargerId={props.data?.uuid}
                  style={{ width: 50, height: 50 }}
                  fallbackSource={require('../../assets/chargerStation.png')}
                  resizeMode="cover"
                  alt="Charger station"
                  circular={true}
                />
              )}
            </Pressable>
          </Animated.View>
          <View style={{ flex: 1 }}>
            <Animated.View style={{ transform: [{ scale: nameScale }] }}>
              <Pressable
                onPress={handleNavigateToCharger}
                disabled={isNavigating}
                onPressIn={() => animatePressIn(nameScale)}
                onPressOut={() => animatePressOut(nameScale)}
              >
                {({ pressed }) => (
                  <Text
                    style={[
                      styles.alignEllipses,
                      {
                        opacity: isNavigating ? 0.6 : (pressed ? 0.8 : 1),
                        color: pressed ? '#39BEB7' : '#FFFFFF',
                      },
                    ]}
                    className="text-base font-bold"
                  >
                    {props.data?.name ? props.data?.name : 'Unknown Charger'}
                  </Text>
                )}
              </Pressable>
            </Animated.View>

            <Text
              style={[styles.textColor3, styles.alignEllipses]}
              className="text-xs font-medium"
            >
              {`${props.data.full_address}`}
            </Text>
            
            {/* Status Badge and Split Badge Row */}
            <View style={styles.badgeRow}>
              {isPendingRegistration ? (
                <Pressable onPress={() => setShowPendingInfoModal(true)}>
                  <ChargerStatusBadge charger={props.data} size="small" />
                </Pressable>
              ) : (
                <ChargerStatusBadge charger={props.data} size="small" />
              )}
              
              {/* Split Badge */}
              {props.data.hasRevenueSplits && props.data.activeSplitCount && props.data.activeSplitCount > 0 && (
                <SplitBadge
                  splitCount={props.data.activeSplitCount}
                  onPress={() => setShowSplitsModal(true)}
                  size="small"
                  showCount={true}
                />
              )}
            </View>
          </View>
        </View>

        {/* Sparkline Chart Container - Hidden for pending chargers */}
        {!isPendingRegistration && (
          <View style={styles.sparklineContainer}>
            {props.sparklineData && Array.isArray(props.sparklineData) && props.sparklineData.length === 7 ? (
              <SparklineChart 
                data={props.sparklineData} 
                width={110} 
                height={70}
                strokeColor="#FF9500"
                strokeWidth={4}
              />
            ) : (
              // Placeholder to maintain layout stability
              <View style={{ width: 110, height: 70 }} />
            )}
            {props.onInsightsPress && (
              <Animated.View style={{ transform: [{ scale: insightsScale }], alignSelf: 'flex-end' }}>
                <Pressable 
                  onPress={() => props.onInsightsPress?.(props.data.uuid ?? '')}
                  onPressIn={() => animatePressIn(insightsScale)}
                  onPressOut={() => animatePressOut(insightsScale)}
                  style={({ pressed }) => ({
                    marginTop: 4, paddingVertical: 2, paddingHorizontal: 4,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  {({ pressed }) => (
                    <Text style={{
                      color: pressed ? '#CC7700' : '#FF9500',
                      fontSize: 11,
                      fontWeight: '600',
                      textDecorationLine: pressed ? 'underline' : 'none',
                    }}>Insights {'>'}</Text>
                  )}
                </Pressable>
              </Animated.View>
            )}
          </View>
        )}
      </View>
      <View className="flex flex-row justify-between items-center p-3">
        <View className="flex flex-row gap-3 items-center">
          <Image
            source={require('../../assets/chargerType.png')}
            style={{ width: 33, height: 33 }}
          />
          <View>
            <Text
              className="text-xs font-normal text-white"
              style={styles.TextColor1}
            >{`${props.data?.charger_model} - ${props.data?.charger_type} Charger`}</Text>
            {props.data?.unit_serial ? (
              <Text style={styles.serialText}>{`S/N: ${props.data.unit_serial}`}</Text>
            ) : null}
            <Text className="text-lg font-semibold text-white">{`$ ${
              props.data?.billing_type === 'hourly'
                ? (props.data?.financials.price_hourly_peak ?? 0)
                : props.data?.financials.price_kwh_peak
            }${props.data?.billing_type === 'hourly' ? '/hr' : '/kWh'} (pk)`}</Text>
          </View>
        </View>
      </View>
      
      {/* Revenue Display - Show for all chargers */}
      <View style={styles.revenueSection}>
        <View style={styles.revenueHeader}>
          <Text style={styles.revenueLabel}>Monthly Revenue</Text>
          {props.data.hasRevenueSplits && props.data.activeSplitCount && props.data.activeSplitCount > 0 && (
            <TouchableOpacity onPress={() => setShowRevenueBreakdown(!showRevenueBreakdown)}>
              <Ionicons 
                name={showRevenueBreakdown ? "chevron-up" : "chevron-down"} 
                size={20} 
                color="#8D8E90" 
              />
            </TouchableOpacity>
          )}
        </View>
        
        {props.data.hasRevenueSplits && props.data.activeSplitCount && props.data.activeSplitCount > 0 && showRevenueBreakdown ? (
          <View style={styles.revenueBreakdown}>
            <View style={styles.revenueRow}>
              <Text style={styles.revenueRowLabel}>Gross Revenue:</Text>
              <Text style={styles.revenueRowValue}>${(props.data.grossRevenue || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.revenueRow}>
              <Text style={[styles.revenueRowLabel, styles.deductionLabel]}>Revenue Splits:</Text>
              <Text style={[styles.revenueRowValue, styles.deductionValue]}>
                -${(props.data.splitDeductions || 0).toFixed(2)}
              </Text>
            </View>
            <View style={[styles.revenueRow, styles.netRevenueRow]}>
              <Text style={styles.netLabel}>Your Net Revenue:</Text>
              <Text style={styles.netValue}>${(props.data.netRevenue || 0).toFixed(2)}</Text>
            </View>
          </View>
        ) : (
          <View style={styles.revenueRow}>
            <Text style={styles.netLabel}>Your Net Revenue:</Text>
            <Text style={styles.netValue}>
              ${(props.data.netRevenue !== undefined ? props.data.netRevenue : props.data.grossRevenue || 0).toFixed(2)}
            </Text>
          </View>
        )}
      </View>
      
      <View className="flex flex-row justify-between items-center px-6 py-3">
        <Animated.View style={{ transform: [{ scale: editScale }] }}>
          <Pressable
            className="px-5 py-3 rounded-full border border-white"
            onPress={() => props.editCharger(props.data)}
            onPressIn={() => animatePressIn(editScale)}
            onPressOut={() => animatePressOut(editScale)}
            style={({ pressed }) => ({
              backgroundColor: pressed ? 'rgba(255,255,255,0.15)' : 'transparent',
            })}
          >
            <Text className="text-xs font-semibold text-white">Edit Charger</Text>
          </Pressable>
        </Animated.View>
        <Animated.View style={{ transform: [{ scale: historyScale }] }}>
          <Pressable
            className="px-5 py-3 bg-[#39BEB7] rounded-full"
            onPress={() => props.showHistory(props.data.uuid ?? '')}
            onPressIn={() => animatePressIn(historyScale)}
            onPressOut={() => animatePressOut(historyScale)}
            style={({ pressed }) => pressed ? { opacity: 0.8 } : undefined}
          >
            <Text className="text-xs font-semibold text-white">
              Charger History
            </Text>
          </Pressable>
        </Animated.View>
      </View>
      
      {/* Pending Charger Info Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showPendingInfoModal}
        onRequestClose={() => setShowPendingInfoModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.pendingModalView}>
            <View style={styles.pendingModalHeader}>
              <Ionicons name="information-circle" size={36} color="#FFA500" />
              <Text style={styles.pendingModalTitle}>Pending Charger</Text>
            </View>
            
            <Text style={styles.pendingModalText}>
              This charger will be associated to your account when you connect it via the CSMS registration on your charger.
            </Text>
            
            <Pressable
              style={styles.pendingModalButton}
              onPress={() => setShowPendingInfoModal(false)}
            >
              <Text style={styles.pendingModalButtonText}>Got it</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
      
      {/* Split List Modal */}
      {props.data.hasRevenueSplits && props.data.activeSplits && (
        <SplitListModal
          visible={showSplitsModal}
          onClose={() => setShowSplitsModal(false)}
          splits={props.data.activeSplits.map(split => ({
            splitId: split.splitId,
            chargerId: props.data.uuid || '',
            hostUserId: props.data.registered_user,
            partnerUserId: '',
            partnerUsername: split.partnerUsername,
            splitPercentage: split.splitPercentage,
            status: 'ACTIVE' as const,
            startDate: '',
            createdAt: '',
            updatedAt: '',
            partner: {
              uuid: '',
              username: split.partnerUsername,
              email: split.partnerEmail,
            },
          }))}
          chargerName={props.data.name}
        />
      )}
    </View>
  );
};

export default MyChargerInfoCard;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  BgColor: {
    backgroundColor: '#51B868',
  },
  BgColor1: {
    backgroundColor: '#39BEB780',
  },
  BgColor2: {
    backgroundColor: '#F97777',
  },
  BgColor3: {
    backgroundColor: '#39BEB7',
  },
  BgColor4: {
    backgroundColor: '#636363',
  },
  TextColor1: {
    color: '#E8E8E9',
  },
  textColor3: {
    color: '#8D8E90',
  },
  serialText: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  filterIcon: {
    height: 17,
    width: 17,
    backgroundColor: '#39BEB7',
  },
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 22,
  },
  modalView: {
    margin: 20,
    backgroundColor: '#292D33',
    borderRadius: 30,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: 290,
    height: 250,
  },
  dropdownTrigger: {
    width: deviceWidth <= 430 ? deviceWidth * 0.4 : 150,
  },
  dropdownContainer: {
    position: 'absolute',
    top: 30,
    left: 0,
    backgroundColor: '#fff',
    borderRadius: 5,
    marginTop: 2,
    width: deviceWidth <= 430 ? deviceWidth * 0.4 : 150,
    zIndex: 1000,
  },
  alignEllipses: Platform.select({
    web: {
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 150,
      whiteSpace: 'nowrap',
    },
    default: {
      fontWeight: '500',
      overflow: 'hidden',
      maxWidth: 150,
    },
  }),

  tooltipContainer: {
    borderRadius: 10,
    padding: 10,
    minWidth: 50,
    maxWidth: 500,
    width: 'auto',
    height: 'auto',
  },
  sparklineContainer: {
    borderRadius: 8,
    padding: 4,
    minWidth: 130,
    alignItems: 'center',
    marginLeft: 8,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: 'white',
    fontSize: 11,
    fontWeight: '600',
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  revenueSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#1A1D23',
    marginHorizontal: 12,
    marginBottom: 8,
    borderRadius: 12,
  },
  revenueHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  revenueLabel: {
    color: '#8D8E90',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  revenueBreakdown: {
    gap: 8,
  },
  revenueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  revenueRowLabel: {
    color: '#E8E8E9',
    fontSize: 14,
    fontWeight: '400',
  },
  revenueRowValue: {
    color: '#E8E8E9',
    fontSize: 14,
    fontWeight: '600',
  },
  deductionLabel: {
    color: '#F59E0B',
  },
  deductionValue: {
    color: '#F59E0B',
  },
  netRevenueRow: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#292D33',
    marginTop: 4,
  },
  netLabel: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  netValue: {
    color: '#51B868',
    fontSize: 16,
    fontWeight: '700',
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  pendingModalView: {
    margin: 20,
    backgroundColor: '#292D33',
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    maxWidth: 340,
    width: '85%',
  },
  pendingModalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  pendingModalTitle: {
    color: '#FFA500',
    fontSize: 20,
    fontWeight: '700',
    marginTop: 8,
  },
  pendingModalText: {
    color: '#E8E8E9',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 24,
  },
  pendingModalButton: {
    backgroundColor: '#39BEB7',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 25,
    width: '100%',
    alignItems: 'center',
  },
  pendingModalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
